import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useUsersStore } from 'src/store/users';
import { useAuthStore } from 'src/store/auth';
import { RoleType, StatusActiveUser } from 'src/constants';

const AdminDetail = () => {
  const { adminId } = useParams<{ adminId: string }>();
  const navigate = useNavigate();
  const { user: currentUser } = useAuthStore();
  const { current, loading, error, loadOne, update, remove } = useUsersStore();

  // Check if user is admin, redirect to root if not
  useEffect(() => {
    if (currentUser && currentUser.role !== RoleType.Admin) {
      navigate('/');
    }
  }, [currentUser, navigate]);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [midName, setMidName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [role, setRole] = useState<number>(1);
  const [status, setStatus] = useState<number>(0);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [changePassword, setChangePassword] = useState(false);
  const [editNewPassword, setEditNewPassword] = useState('');
  const [editConfirmPassword, setEditConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');

  useEffect(() => {
    if (adminId) {
      loadOne(adminId);
    }
  }, [adminId, loadOne]);

  // Update form fields when current user data loads
  useEffect(() => {
    if (current) {
      setFirstName(current.firstName || '');
      setLastName(current.lastName || '');
      setMidName(current.midName || '');
      setEmail(current.email || '');
      setPhone(current.phone || '');
      setRole(current.role);
      setStatus(current.status);
    }
  }, [current]);

  // Function to get role name from role value
//   const getRoleName = (roleValue: number): string => {
//     const roleEntry = Object.entries(RoleType).find(([_, value]) => value === roleValue);
//     return roleEntry ? roleEntry[0] : 'Unknown';
//   };

  // Function to check if user is current user
  const isCurrentUser = (userId: unknown): boolean => {
    return currentUser?.id === userId;
  };

  // Function to validate password fields
  const validatePasswords = (): boolean => {
    if (!changePassword) {
      setPasswordError('');
      return true;
    }

    if (!editNewPassword.trim()) {
      setPasswordError('Password is required');
      return false;
    }

    if (editNewPassword.length < 8 || !/[A-Z]/.test(editNewPassword) || !/[a-z]/.test(editNewPassword) || !/[0-9]/.test(editNewPassword) || !/[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\\/`~;']/.test(editNewPassword)) {
      setPasswordError('Password must be at least 8 characters and include uppercase and lowercase letters, numbers, and symbols');
      return false;
    }

    if (editNewPassword !== editConfirmPassword) {
      setPasswordError('Passwords do not match');
      return false;
    }

    setPasswordError('');
    return true;
  };

  const handleUpdate = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!adminId) return;

    // Validate passwords if change password is checked
    if (!validatePasswords()) {
      return;
    }

    // Prepare update data
    const updateData: any = { firstName, lastName, midName, email, phone, role, status };
    
    // Add password to update data if change password is checked
    if (changePassword && editNewPassword.trim()) {
      updateData.password = editNewPassword;
    }

    await update(adminId, updateData);
    navigate('/admins');
  };

  const handleDelete = async () => {
    if (!adminId) return;
    await remove(adminId);
    navigate('/admins');
  };

  const handleBackToList = () => {
    navigate('/admins');
  };

  if (loading) {
    return (
      <div className="flex-1 h-full rounded-[32px] p-2 id-card-container overflow-y-auto max-lg:my-2.5 relative flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  if (error || !current) {
    return (
      <div className="flex-1 h-full rounded-[32px] p-2 id-card-container overflow-y-auto max-lg:my-2.5 relative flex items-center justify-center">
        <div className="text-lg text-red-600">{error || 'Admin not found'}</div>
      </div>
    );
  }

  return (
    <div className="flex-1 h-full rounded-[32px] p-2 id-card-container overflow-y-auto max-lg:my-2.5 relative">
      {/* Header */}
      <div className="px-8 py-6 flex items-center justify-between max-md:p-2">
        <div className="flex items-center gap-4">
          <button
            onClick={handleBackToList}
            className="p-2 hover:bg-gray-100 flex items-center rounded-full"
          >
            <img src="/icons/left.svg" alt="back" />
          </button>
          <h1 className="id-card-title font-medium max-lg:!text-2xl max-md:!text-xl">
            {current.firstName} {current.lastName}
          </h1>
        </div>
        {!isCurrentUser(current.id) && (
          <div className="flex gap-2">
            <button
              onClick={() => setConfirmOpen(true)}
              className="flex-1 px-4 py-2 bg-secondary-bg text-white rounded-full hover:bg-opacity-90 transition-colors" 
            >
              Delete Admin
            </button>
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="px-8 py-6 max-md:px-2">
        <div className="bg-white-overlay big-corner p-6 max-md:p-4">
          <form onSubmit={handleUpdate} className="grid gap-4 max-w-3xl">
            <div className="grid grid-cols-2 gap-3">
              <input 
                className="px-3 py-2 rounded-md bg-white text-[var(--gray-800)] border" 
                placeholder="First name" 
                value={firstName} 
                onChange={(e) => setFirstName(e.target.value)} 
                required 
              />
              <input 
                className="px-3 py-2 rounded-md bg-white text-[var(--gray-800)] border" 
                placeholder="Last name" 
                value={lastName} 
                onChange={(e) => setLastName(e.target.value)} 
                required 
              />
              <input 
                className="px-3 py-2 rounded-md bg-white text-[var(--gray-800)] border" 
                placeholder="Middle name" 
                value={midName} 
                onChange={(e) => setMidName(e.target.value)} 
              />
              <input 
                className="px-3 py-2 rounded-md bg-white text-[var(--gray-800)] border" 
                placeholder="Email" 
                type="email" 
                value={email} 
                onChange={(e) => setEmail(e.target.value)} 
                required 
              />
              <input 
                className="px-3 py-2 rounded-md bg-white text-[var(--gray-800)] border" 
                placeholder="Phone" 
                value={phone} 
                onChange={(e) => setPhone(e.target.value)} 
              />
              <select 
                className="px-3 py-2 rounded-md bg-white text-[var(--gray-800)] border" 
                value={role} 
                onChange={(e) => setRole(Number(e.target.value))}
              >
                <option value={RoleType.User}>User</option>
                <option value={RoleType.Admin}>Admin</option>
                <option value={RoleType.Manager}>Manager</option>
                <option value={RoleType.AI}>AI</option>
              </select>
              <select 
                className="px-3 py-2 rounded-md bg-white text-[var(--gray-800)] border" 
                value={status} 
                onChange={(e) => setStatus(Number(e.target.value))}
              >
                <option value={StatusActiveUser.Active}>Active</option>
                <option value={StatusActiveUser.Banned}>Banned</option>
                <option value={StatusActiveUser.Pending}>Pending</option>
              </select>
            </div>
            
            {/* Change Password Section */}
            <div className="mt-4">
              <div className="flex items-center gap-2 mb-3">
                <input 
                  id="change-password" 
                  type="checkbox" 
                  checked={changePassword} 
                  onChange={(e) => {
                    setChangePassword(e.target.checked);
                    if (!e.target.checked) {
                      setEditNewPassword('');
                      setEditConfirmPassword('');
                      setPasswordError('');
                    }
                  }} 
                />
                <label htmlFor="change-password" className="text-sm">Change password</label>
              </div>
              
              {changePassword && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <input 
                      className="px-3 py-2 rounded-md bg-white text-[var(--gray-800)] border w-full"
                      placeholder="New password"
                      type="password"
                      value={editNewPassword}
                      onChange={(e) => {
                        setEditNewPassword(e.target.value);
                        // Real-time validation
                        if (editConfirmPassword && e.target.value !== editConfirmPassword) {
                          setPasswordError('Passwords do not match');
                        } else if (passwordError) {
                          setPasswordError('');
                        }
                      }}
                      required
                    />
                  </div>
                  <div>
                    <input 
                      className="px-3 py-2 rounded-md bg-white text-[var(--gray-800)] border w-full"
                      placeholder="Confirm password"
                      type="password"
                      value={editConfirmPassword}
                      onChange={(e) => {
                        setEditConfirmPassword(e.target.value);
                        // Real-time validation
                        if (editNewPassword && e.target.value !== editNewPassword) {
                          setPasswordError('Passwords do not match');
                        } else if (passwordError) {
                          setPasswordError('');
                        }
                      }}
                      required
                    />
                  </div>
                </div>
              )}
              
              {passwordError && (
                <p className="text-sm text-red-600 mt-2">{passwordError}</p>
              )}
            </div>
            
            <div className="flex gap-3 pt-4">
              {/* <button 
                type="button" 
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-full hover:bg-gray-300 transition-colors" 
                onClick={handleBackToList}
              >
                Cancel
              </button> */}
              <button 
                type="submit" 
                className="flex-1 px-4 py-2 bg-secondary-bg text-white rounded-full hover:bg-opacity-90 transition-colors" 
                disabled={loading || !!passwordError}
              >
                {loading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Delete Confirmation */}
      {confirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setConfirmOpen(false)} />
          <div className="relative bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Delete Admin</h3>
            <p className="text-gray-600 mb-6">Are you sure you want to delete this admin? This action cannot be undone.</p>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setConfirmOpen(false)}
                className="id-card-button-secondary px-4 py-2 rounded-full"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className={"id-card-button-primary w-full px-4 py-2 rounded-full"}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDetail;
